import { t } from "./i18n";

// small money graphs drawn as plain elements so they stay crisp at any width and follow the theme.
// one axis, thin bars, numbers in ink colours, a tap or hover on a bar shows what it is

export interface ChartSeries {
  name: string;
  // a theme colour, like "var(--chi)"
  color: string;
  values: number[];
}

export interface ColumnChart {
  labels: string[];
  // shown under the bar when it's tapped, one per label
  details?: string[];
  series: ChartSeries[];
  format: (value: number) => string;
  // short numbers for the axis
  axis?: (value: number) => string;
  // the label to light up, usually this month
  current?: number;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

// a round step above the biggest value so the gridlines land on tidy numbers
function niceTop(max: number): number {
  if (max <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(max));
  const step = [1, 2, 2.5, 5, 10].find((mult) => mult * power >= max) || 10;
  return step * power;
}

function legend(series: ChartSeries[]): HTMLElement {
  const row = el("div", "chart-legend");
  for (const item of series) {
    const key = el("span", "chart-key");
    const swatch = el("i");
    swatch.style.background = item.color;
    key.append(swatch, document.createTextNode(item.name));
    row.append(key);
  }
  return row;
}

// columns per label, side by side when there's more than one series
export function drawColumns(box: HTMLElement, chart: ColumnChart): void {
  const max = Math.max(0, ...chart.series.flatMap((item) => item.values));
  const top = niceTop(max);
  const axis = chart.axis || chart.format;
  const frame = el("div", "chart");
  if (chart.series.length > 1) frame.append(legend(chart.series));

  const plot = el("div", "chart-plot");
  const grid = el("div", "chart-grid");
  for (const step of [1, 0.5, 0]) {
    const line = el("span", "chart-line");
    line.style.bottom = `${step * 100}%`;
    line.dataset.value = step ? axis(top * step) : "";
    grid.append(line);
  }
  const cols = el("div", "chart-cols");
  const tip = el("div", "chart-tip");
  tip.hidden = true;

  chart.labels.forEach((label, at) => {
    const col = el("button", "chart-col");
    col.type = "button";
    if (at === chart.current) col.classList.add("now");
    const bars = el("span", "chart-bars");
    for (const item of chart.series) {
      const bar = el("i", "chart-bar");
      const value = item.values[at] || 0;
      bar.style.height = value > 0 ? `max(2px, ${(value / top) * 100}%)` : "0";
      bar.style.background = item.color;
      bars.append(bar);
    }
    const name = el("span", "chart-label", label);
    col.append(bars, name);
    const lines = chart.series.map((item) => `${chart.series.length > 1 ? `${item.name} ` : ""}${chart.format(item.values[at] || 0)}`);
    col.setAttribute("aria-label", `${label}: ${lines.join(", ")}`);
    const show = () => {
      tip.replaceChildren(el("b", "", label), ...lines.map((line) => el("span", "", line)));
      if (chart.details?.[at]) tip.append(el("small", "", chart.details[at]));
      tip.hidden = false;
      const left = (col.offsetLeft + col.offsetWidth / 2) / Math.max(1, cols.offsetWidth);
      tip.style.left = `${Math.min(80, Math.max(20, left * 100))}%`;
      cols.querySelectorAll(".chart-col.picked").forEach((item) => item.classList.remove("picked"));
      col.classList.add("picked");
    };
    col.addEventListener("pointerenter", (event) => {
      if (event.pointerType === "mouse") show();
    });
    col.addEventListener("click", show);
    col.addEventListener("focus", show);
    cols.append(col);
  });
  plot.addEventListener("pointerleave", (event) => {
    if (event.pointerType !== "mouse") return;
    tip.hidden = true;
    cols.querySelectorAll(".chart-col.picked").forEach((item) => item.classList.remove("picked"));
  });
  plot.append(grid, cols, tip);
  frame.append(plot);
  box.replaceChildren(frame);
}

export interface BarRow {
  label: string;
  value: number;
  // the small text after the number, like "3 comms"
  note?: string;
  color?: string;
}

// a ranked list of horizontal bars with the numbers written on them, the longest is the biggest
export function drawBars(box: HTMLElement, rows: BarRow[], format: (value: number) => string, empty = ""): void {
  if (!rows.length) {
    box.replaceChildren(el("p", "empty", empty || t("nothing yet")));
    return;
  }
  const max = Math.max(...rows.map((row) => row.value), 0) || 1;
  const list = el("div", "hbars");
  for (const row of rows) {
    const line = el("div", "hbar");
    const head = el("div", "hbar-head");
    head.append(el("span", "hbar-name", row.label), el("span", "hbar-num", `${format(row.value)}${row.note ? ` · ${row.note}` : ""}`));
    const track = el("div", "hbar-track");
    const fill = el("i");
    fill.style.width = row.value > 0 ? `max(3px, ${(row.value / max) * 100}%)` : "0";
    if (row.color) fill.style.background = row.color;
    track.append(fill);
    line.append(head, track);
    list.append(line);
  }
  box.replaceChildren(list);
}

// one segmented bar split into parts, with a key under it. for "where the money is" style splits
export function drawSplit(box: HTMLElement, parts: BarRow[], format: (value: number) => string): void {
  const shown = parts.filter((part) => part.value > 0);
  const total = shown.reduce((sum, part) => sum + part.value, 0);
  const frame = el("div", "split");
  const bar = el("div", "split-bar");
  for (const part of shown) {
    const piece = el("i");
    piece.style.flexGrow = String(part.value);
    piece.style.background = part.color || "var(--chi)";
    piece.title = `${part.label} ${format(part.value)}`;
    bar.append(piece);
  }
  if (!total) bar.classList.add("empty");
  const keys = el("div", "chart-legend");
  for (const part of parts) {
    const key = el("span", "chart-key");
    const swatch = el("i");
    swatch.style.background = part.color || "var(--chi)";
    key.append(swatch, document.createTextNode(`${part.label} ${format(part.value)}${part.note ? ` · ${part.note}` : ""}`));
    keys.append(key);
  }
  frame.append(bar, keys);
  box.replaceChildren(frame);
}

// a small gauge from the meters strip, v is 0 to 1
export function setGauge(meter: HTMLElement, text: string, fraction: number): void {
  meter.style.setProperty("--v", String(Math.max(0, Math.min(1, fraction))));
  const num = meter.querySelector(".meter-num");
  if (num) num.textContent = text;
}
